import { ACTIVE_SYNC_BASIC_ID, ACTIVE_SYNC_PLUS_ID } from '@brightlocal/shared';

import { BUILDER_STEPS } from 'shared/consts';

import type {
  BuilderDeliverySpeed,
  BuilderPreference,
  BuilderQuestionServe,
  BuilderStep,
  PriceBreakdown
} from './types';

// Payment-method values live with the Old Platform checkout redirect (the
// Finalize `?paymentMethod=` param), re-exported here for the wizard's local use.
export { PAYMENT_METHOD } from 'shared/modules/old-platform-redirect';

export const BUILDER_STORAGE_KEY = 'citation_builder_flow';

/** `item_category` carried on every CB ecommerce line item. */
export const CB_ECOMMERCE_ITEM_CATEGORY = 'Citation Builder';

/** dataLayer key the CB campaign id is reported under on ecommerce pushes. */
export const CB_JOURNEY_ID_KEY = 'GACBCampaignId';

/**
 * `item_id` / `item_name` per CB component. The bundle is renamed from the
 * estimate's `package`, too vague in a CB-only report.
 *
 * Publishers and Active Sync are absent — their ids come from the estimate.
 */
export const CB_ECOMMERCE_ITEMS = {
  BUNDLE: { id: 'citations_bundle', name: 'Citations Bundle' },
  DUPLICATE_REMOVAL: { id: 'duplicates_removal', name: 'Duplicate Removal' },
  EXPRESS_DELIVERY: { id: 'express', name: 'Express Delivery' }
} as const;

/**
 * GA4 `item_name` per Active Sync SKU — each SKU is its own product, so naming
 * both "Active Sync" would leave the rows separable only by id. Typed as a plain
 * record so a slug the estimate reports but we haven't named still indexes.
 */
export const ACTIVE_SYNC_ITEM_NAMES: Record<string, string> = {
  [ACTIVE_SYNC_BASIC_ID]: 'Active Sync',
  [ACTIVE_SYNC_PLUS_ID]: 'Active Sync Plus'
};

/** Named delivery-speed values, so the `standard`/`express` literals live in one place. */
export const DELIVERY_SPEED = {
  STANDARD: 'standard',
  EXPRESS: 'express'
} as const satisfies Record<string, BuilderDeliverySpeed>;

/** Delivery speed applied when the customer hasn't explicitly picked one. Cross-step (Delivery step + the step footer). */
export const DEFAULT_DELIVERY_SPEED: BuilderDeliverySpeed = DELIVERY_SPEED.STANDARD;

/**
 * Named serve-model answer values, so the `fixed` / `service` / `both` literals
 * live in one place. The `BuilderQuestionServe` union in `types.ts` stays the
 * source of truth; this `satisfies` it.
 */
export const SERVE_MODEL = {
  FIXED: 'fixed',
  SERVICE: 'service',
  BOTH: 'both'
} as const satisfies Record<string, BuilderQuestionServe>;

/**
 * Named build-preference values, so the preference-id literals live in one
 * place. The `BuilderPreference` union in `types.ts` stays the source of
 * truth; this `satisfies` it.
 */
export const BUILDER_PREFERENCE = {
  HIDE_ADDRESS: 'hideAddress',
  SKIP_PHONE_VERIFICATION: 'skipPhoneVerification',
  INSTANT_LISTING: 'instantListing',
  NO_FOLLOW_UP: 'noFollowUp'
} as const satisfies Record<string, BuilderPreference>;

/** Stand-in price for a basket line the estimate doesn't price yet. */
export const ZERO_PRICE: PriceBreakdown = { usd: 0, credits: 0 };

/**
 * Page size for the campaign-packages fetch. The packages endpoint caps
 * `num_per_page` at 500 (lower than the citations endpoint), and the list is
 * tiny (a handful of bundles), so a single page covers it.
 */
export const CAMPAIGN_PACKAGES_PAGE_SIZE = 500;

/**
 * Page size for the available-citations (directory catalog) fetch. BE supports
 * pagination but the dataset is small enough (~hundreds) that a single large
 * page covers it — matches the old platform's UX.
 */
export const AVAILABLE_CITATIONS_PAGE_SIZE = 1000;

/** Preferred default bundle — `resolveDefaultPackage` picks the package at this citation count, else the largest offered. */
export const DEFAULT_BUNDLE_SIZE = 35;

/** BE `packageName` for the dynamically-sized "everything available" package — shown with a label, not its citation count. */
export const ALL_PACKAGE_NAME = 'all';

/**
 * Reference/catalog data (publisher prices, saved campaign order + aggregator
 * catalog) is country- and campaign-scoped and only changes through our own
 * mutations, which invalidate the cache. It never goes stale on its own within a
 * session, so hold it indefinitely instead of refetching on every step hop —
 * mirrors `LOCATION_STALE_TIME`.
 */
export const REFERENCE_DATA_STALE_TIME = Infinity;

/**
 * Account-scoped, mildly dynamic data (credit balance, health score) is stable
 * enough to reuse across step navigation but shouldn't be frozen for the whole
 * session — a 5-minute window avoids a refetch on every step hop while still
 * picking up a change on a later revisit.
 */
export const ACCOUNT_DATA_STALE_TIME = 5 * 60 * 1000;

/**
 * How long a restore may sit unfinished on a billable step before we call it failed
 * and offer a retry. A hung scan has no failed state on the BE, so this bound is the
 * only way out of an otherwise indefinite wait. 30s is a first pass, not a measured
 * threshold.
 */
export const RESTORE_TIMEOUT_MS = 30 * 1000;

/** `eventLabel` values for `scan_error` — separates a technical failure from an unsupported country. */
export const CB_SCAN_ERROR_REASONS = {
  FAILED: 'failed',
  NO_LOOKUP_COUNTRY: 'no_lookup_country'
} as const;

/**
 * `eventLabel` values for `error_api` — the user-visible failed state, not the
 * backend error. Only the states a first release acts on are wired.
 */
export const CB_API_ERROR_REASONS = {
  /** Confirming the order at checkout failed. */
  ORDER_CONFIRMATION_FAIL: 'order_confirmation_fail'
} as const;

/**
 * Rows per page in the Build and Fix citation tables. Bounds per-toggle render
 * cost — the DS DataTable re-renders every row in the DOM on any table state
 * change — and keeps both lists paging identically. Matches the All Locations table.
 */
export const CITATION_TABLE_PAGE_SIZE = 20;

/** One of the `scan_error` reasons. */
export type CbScanErrorReason = (typeof CB_SCAN_ERROR_REASONS)[keyof typeof CB_SCAN_ERROR_REASONS];

/** One of the `error_api` reasons. */
export type CbApiErrorReason = (typeof CB_API_ERROR_REASONS)[keyof typeof CB_API_ERROR_REASONS];

/**
 * Step names as the tracking spec labels them, keyed by the wizard's step id.
 *
 * The names follow the spec, not the store — `delivery` reports as `speed` and
 * `review` as `summary`, so don't align them to the ids. `scan` is absent: it is
 * never an edit target on the Review step.
 */
export const CB_STEP_TRACKING_NAMES: Partial<Record<BuilderStep, string>> = {
  [BUILDER_STEPS.FIX]: 'fix',
  [BUILDER_STEPS.BUILD]: 'build',
  [BUILDER_STEPS.AGGREGATORS]: 'aggregators',
  [BUILDER_STEPS.DELIVERY]: 'speed',
  [BUILDER_STEPS.REVIEW]: 'summary'
};

/**
 * Preference names as the tracking spec labels them, keyed by the store's
 * preference id. `eventAction` on `preference_filter_toggled` is the name plus
 * the resulting state, e.g. `hide_address_on`.
 *
 * The names follow the spec, not the store — `noFollowUp` reports as
 * `no_extra_steps`, so don't align them to the ids.
 */
export const CB_PREFERENCE_TRACKING_NAMES = {
  hideAddress: 'hide_address',
  skipPhoneVerification: 'skip_phone_verification',
  instantListing: 'instant_listing',
  noFollowUp: 'no_extra_steps'
} as const satisfies Record<BuilderPreference, string>;

/**
 * Width every wizard surface centres on — the step body, the basket beside it and
 * the footer actions below. Shared so the three can't drift apart; when they did,
 * the basket sat at the page edge while the content centred on its own narrower box.
 */
export const STEP_LAYOUT_CONTAINER = 'mx-auto w-full max-w-6xl';

/**
 * Content + basket track. `minmax(0,1fr)` rather than `1fr` so a wide table in the
 * content column can't push the basket off the grid.
 */
export const STEP_LAYOUT_GRID = 'grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6';

/** Help-centre article the duplicate-removal campaign term links to. */
export const DUPLICATE_REMOVAL_HELP_URL =
  'https://help.brightlocal.com/hc/en-us/articles/224910407-How-does-Citation-Builder-handle-the-removal-of-duplicate-listings-';

/** Standard campaign terms listed on Review, in display order; each key resolves to its copy. */
export const CAMPAIGN_TERM_KEYS = [
  'submissionWindow',
  'informationAccuracy',
  'profileContent',
  'duplicateRemoval',
  'informationChanges',
  'alternativeSites',
  'liveListings'
] as const;

/** Express-only campaign terms, shown above the standard ones when Express delivery is selected. */
export const EXPRESS_CAMPAIGN_TERM_KEYS = [
  'expressSubmissionWindow',
  'expressSubmissionTarget',
  'expressOnHold',
  'expressAggregators',
  'expressRefund'
] as const;

/** The standard term the Express 24-hour terms replace, so the two timeframes never show together. */
export const STANDARD_SUBMISSION_WINDOW_TERM_KEY = 'submissionWindow' satisfies (typeof CAMPAIGN_TERM_KEYS)[number];

/**
 * Minimum width the citation tables keep before the DS `DataTable` scrolls
 * horizontally. Their columns are percentage-based, so without a floor the site
 * and issue text crushes to a few characters on a phone.
 */
export const CITATION_TABLE_MIN_WIDTH = '640px';
